import {TimelineLite} from "gsap";
import {IMToWildFeature} from "../server/data/IWalkOfShameData";
import {WalkOfShame} from "../WalkOfShame";
import {WalkOfShameSymbolState} from "../symbol/WalkOfShameSymbolConfig";
import {FullMidCharacterOverlay} from "../component/FullMidCharacterOverlay";
import {Layers, WalkOfShameLayers} from "./WalkOfShameLayers";
import {WalkOfShameSymbol} from "../symbol/WalkOfShameSymbol";
import {WalkOfShameGameConfig} from "../WalkOfShameGameConfig";
import {WalkOfShameGameMode} from "../server/WalkOfShameGameMode";
import {WalkOfShameHelper} from "../../core/utils/WalkOfShameHelper";
import {WalkOfShameSound} from "../sound/WalkOfShameSound";
import {MathHelper} from "../../core/utils/MathHelper";

export class MToWildView extends PIXI.Container {
    public canApplyNoWin: boolean = false;
    private _items: FullMidCharacterOverlay[] = [];
    private _xPositions: number[] = [0, 216, 360, 504, 648];
    private _walkPrevSound: string = "";
    private _m1PrevSounds: string[] = [];
    private _m2PrevSounds: string[] = [];
    private _m3PrevSounds: string[] = [];
    private _m4PrevSounds: string[] = [];

    constructor() {
        super();

        WalkOfShameLayers.getLayer(Layers.M_TO_WILD)?.addChild(this);
        this.y = 360;
    }

    public getAnimation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            featureData: IMToWildFeature[] = WalkOfShame.model.parsedGameData.mToWildFeature,
            isWin: boolean = (WalkOfShame.model.parsedGameData.betWins.length > 0),
            isReSpinTriggered: boolean = ((WalkOfShame.model.parsedGameData.mode === WalkOfShameGameMode.NORMAL || WalkOfShame.model.parsedGameData.mode === WalkOfShameGameMode.SUPER_FREESPIN) && (WalkOfShame.model.parsedGameData.nextMode.indexOf("RESPIN") > -1));

        tl.to(this, {
            duration: 0.1
        });
        tl.add(() => {
            for (let ctr: number = 0; ctr < featureData.length; ctr++) {
                const reel: number = featureData[ctr].reel,
                    characterName: string = featureData[ctr].symbol;

                const fullMidOverlay: FullMidCharacterOverlay = new FullMidCharacterOverlay(characterName, reel, isWin);
                fullMidOverlay.position.set(this._xPositions[reel], 25);
                this._items.push(fullMidOverlay);
                this.addChild(fullMidOverlay);
            }
        });
        if (isWin) {
            tl.to(this, {
                duration: 1.6
            });
        }
        tl.add(() => {
            if (this.canApplyNoWin) {
                this.applyNoWin();
            }

            for (let ctr: number = 0; ctr < featureData.length; ctr++) {
                const allSymbols: WalkOfShameSymbol[] = WalkOfShame.reels[featureData[ctr].reel].reelView.allSymbols;

                for (let idx: number = 1; idx < allSymbols.length - 1; idx++) {
                    allSymbols[idx].visible = false;
                }
            }
            this._items.forEach((item: FullMidCharacterOverlay) => {
                item.symbolCount.visible = true;
            });
        });

        if (isReSpinTriggered) {
            // need this to be executed later, after the sticky S2 animation is called.
            tl.add(() => {
                WalkOfShame.stickyS2.toggleS2(true, WalkOfShame.model.parsedGameData.s2SymId);
            }, "+=0.01");

            tl.add([
                () => {
                    WalkOfShameSound.soundPlayer.playTrack({
                        name: WalkOfShameSound.X_SPLIT_LIGHTER_ON
                    });
                },
                WalkOfShame.stickyS2.playS2Animation("respin_trigger", false)
            ]);
        }

        if (WalkOfShame.model.parsedGameData.mode !== WalkOfShameGameMode.NORMAL && WalkOfShame.model.parsedGameData.mode !== WalkOfShameGameMode.RESPIN) {
            tl.addLabel("doMultiplierUpdate");
            for (let ctr: number = 0; ctr < featureData.length; ctr++) {
                tl.add(this.updateMultiplier(featureData[ctr].reel, featureData[ctr].symbol), "doMultiplierUpdate");
            }
            const multiValue: number = WalkOfShame.multiplier.multiValue;
            tl.add([
                () => {
                    WalkOfShameSound.soundPlayer.playTrack({
                        name: WalkOfShameSound.MULTIPLIER_UP.next()
                    });
                },
                WalkOfShame.multiplier.updateMultiplierText((multiValue + featureData.length), true)
            ]);
        }

        return tl;
    }

    public onInit(featureData: IMToWildFeature[]): void {
        this._items = [];
        for (let ctr: number = 0; ctr < featureData.length; ctr++) {
            const reel: number = featureData[ctr].reel,
                characterName: string = featureData[ctr].symbol,
                fullMidOverlay: FullMidCharacterOverlay = new FullMidCharacterOverlay(characterName, reel, false),
                allSymbols: WalkOfShameSymbol[] = WalkOfShame.reels[reel].reelView.allSymbols;

            fullMidOverlay.position.set(this._xPositions[reel], 25);
            this._items.push(fullMidOverlay);
            this.addChild(fullMidOverlay);

            for (let idx: number = 1; idx < allSymbols.length - 1; idx++) {
                allSymbols[idx].visible = false;
            }
        }
    }

    public doWalkAnimation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let removableItem: FullMidCharacterOverlay | undefined,
            canPlaySound: boolean = false;

        this._items.forEach((item: FullMidCharacterOverlay) => {
            if (item.reelId > 1) {
                canPlaySound = true;

                item.x = this._xPositions[item.reelId] + 2;
                const allSymbols: WalkOfShameSymbol[] = WalkOfShame.reels[item.reelId].reelView.allSymbols,
                    charName: string = item.characterName;

                allSymbols.forEach((sym: WalkOfShameSymbol) => {
                    sym.visible = false;
                });

                if (charName === "M1") {
                    this._m1PrevSounds = this.setSound(this._m1PrevSounds, WalkOfShameSound.WILD_WALKING_M1);
                } else if (charName === "M2") {
                    this._m2PrevSounds = this.setSound(this._m2PrevSounds, WalkOfShameSound.WILD_WALKING_M2);
                } else if (charName === "M3") {
                    this._m3PrevSounds = this.setSound(this._m3PrevSounds, WalkOfShameSound.WILD_WALKING_M3);
                } else if (charName === "M4") {
                    this._m4PrevSounds = this.setSound(this._m4PrevSounds, WalkOfShameSound.WILD_WALKING_M4);
                }

                item.tint = 0xffffff;
                item.symbolCount.visible = false;
                item.updateSymbolCountValue(4);
                item.symbolCount.tint = 0xffffff;
                item.reelId -= 1;

                tl.add(item.walk(), 0);
                tl.add(() => {
                    WalkOfShameGameConfig.WALK_COMPLETED = true;

                    item.symbolCount.x = (144 / 2 - item.symbolCount.width / 2 - 144 - 5);
                    item.symbolCount.visible = true;
                });
            } else if (item.reelId === 1) {
                this.updateSymbol(item);
                removableItem = item;
            }
        });

        if (canPlaySound) {
            let sound: string = WalkOfShameSound.WILD_WALKING[MathHelper.randomNumberInRange(0, WalkOfShameSound.WILD_WALKING.length - 1)];

            while (this._walkPrevSound === sound) {
                sound = WalkOfShameSound.WILD_WALKING[MathHelper.randomNumberInRange(0, WalkOfShameSound.WILD_WALKING.length - 1)];
            }
            WalkOfShameSound.soundPlayer.playTrack({
                name: sound
            });
            this._walkPrevSound = sound;
        }

        if (removableItem) {
            this._items.splice(this._items.indexOf(removableItem), 1);
        }

        WalkOfShameGameConfig.WALK_COMPLETED = (this._items.length === 0);

        return tl;
    }

    public turnOffAllCharacters(): void {
        this._items.forEach((item: FullMidCharacterOverlay) => {
            if (item.visible) {
                this.updateSymbol(item);
            }
        });
        this._items = [];
        this.removeChildren();

        this.canApplyNoWin = false;
    }

    public enableSymbolCount(reel: number): void {
        this._items.forEach((item: FullMidCharacterOverlay) => {
            if (item.reelId === reel && item.visible) {
                item.updateSymbolCountValue(8);

                WalkOfShameGameConfig.SYMBOLS_COUNT_IN_REELS[reel] = 8;
            }
        });
    }

    public ifFullSymExistsOnReel(reelToCheck: number): boolean {
        for (let ctr: number = 0; ctr < this._items.length; ctr++) {
            if (this._items[ctr].reelId === reelToCheck) {
                return true;
            }
        }
        return false;
    }

    public playWin(reelToCheck: number, animName: string): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let item: FullMidCharacterOverlay;

        for (let ctr: number = 0; ctr < this._items.length; ctr++) {
            item = this._items[ctr];

            if (item.reelId === reelToCheck && item.visible) {
                tl.add(() => {
                    if (item.x !== this._xPositions[reelToCheck]) {
                        item.x = this._xPositions[reelToCheck];
                        item.symbolCount.x += 144;
                    }
                });
                tl.add(item.win(animName));

                return tl;
            }
        }

        return tl;
    }

    public skip(): void {
        this._items.forEach((item: FullMidCharacterOverlay) => {
            item.skip();
        });
    }

    private applyNoWin(): void {
        for (let ctr: number = 0; ctr < this._items.length; ctr++) {
            if (this._items[ctr].reelId === 4) {
                this._items[ctr].tint = 0x555555;
                this._items[ctr].symbolCount.tint = 0x555555;
                return;
            }
        }
    }

    private updateMultiplier(reel: number, charName: string): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(WalkOfShame.assetLoader.getSpineAsset("multiplierEffect"));

        item.x = 360;

        tl.add(() => {
            this.addChild(item);

            WalkOfShameSound.soundPlayer.playTrack({
                name: WalkOfShameSound.MULTIPLIER_WOOSH.next()
            });
        });
        tl.add(WalkOfShameHelper.createTimelineForAnimation(item, 0, "reel0" + (reel + 1) + charName, false));
        tl.add(() => {
            this.removeChild(item);
        });

        return tl;
    }

    private setSound(sounds: string[], walkingSounds: string[]): string[] {
        let sound: string = walkingSounds[MathHelper.randomNumberInRange(0, walkingSounds.length - 1)];

        while (sounds.indexOf(sound) > -1) {
            sound = walkingSounds[MathHelper.randomNumberInRange(0, walkingSounds.length - 1)];
        }
        WalkOfShameSound.soundPlayer.playTrack({
            name: sound
        });
        sounds.push(sound);

        if (sounds.length === walkingSounds.length) {
            sounds = [];
        }

        return sounds;
    }

    private updateSymbol(item: FullMidCharacterOverlay): void {
        let symbol: WalkOfShameSymbol = WalkOfShame.reels[item.reelId].getSymbol(3);
        const replacedSymbol: WalkOfShameSymbol = symbol.replaceSymbol(item.characterName, WalkOfShameSymbolState.FULL);

        if (item.reelId === 4 && this.canApplyNoWin) {
            replacedSymbol.tint = 0x555555;
        }

        item.visible = false;
        item.reelId = -1;
    }
}